/**
 * Request security implementation for web.
 *
 * Implements the protocol defined in backend/REQUEST_SECURITY_PROTOCOL.md
 * using the Web Crypto API (crypto.subtle) instead of js-sha256.
 *
 * Canonical string format (newline-joined):
 *   METHOD\nPATH\nCANONICAL_QUERY\nBODY_SHA256\nTIMESTAMP\nNONCE
 */

// ── Server time offset (serverTime - localTime) in ms ─────────────

let _serverTimeOffsetMs = 0;

export function updateServerTimeOffset(serverTimeMs: number): void {
  _serverTimeOffsetMs = serverTimeMs - Date.now();
}

function getAdjustedTimestamp(): number {
  return Math.round(Date.now() + _serverTimeOffsetMs);
}

// ── Nonce (UUID-v4-like hex) ──────────────────────────────────────

function generateNonce(): string {
  const h = '0123456789abcdef';
  const seg = [8, 4, 4, 4, 12];
  return seg
    .map((len) =>
      Array.from({ length: len }, () => h[Math.floor(Math.random() * 16)]).join(''),
    )
    .join('-');
}

// ── Go-compatible url.QueryEscape ─────────────────────────────────
// Go unreserved: A-Z a-z 0-9 - _ . ~  ;  space → +
// JS encodeURIComponent additionally leaves ! ' ( ) * unencoded
// and encodes space as %20 — patch both differences here.

function goQueryEscape(s: string): string {
  return encodeURIComponent(s)
    .replace(/%20/g, '+')
    .replace(/[!'()*]/g, (c) => '%' + c.charCodeAt(0).toString(16).toUpperCase());
}

// ── Canonical query (matches Go canonicalQuery) ───────────────────

function canonicalQuery(params: URLSearchParams): string {
  const grouped = new Map<string, string[]>();
  params.forEach((value, key) => {
    const existing = grouped.get(key);
    if (existing) {
      existing.push(value);
    } else {
      grouped.set(key, [value]);
    }
  });

  if (grouped.size === 0) return '';

  const sortedKeys = Array.from(grouped.keys()).sort();
  const pairs: string[] = [];
  for (const key of sortedKeys) {
    const values = grouped.get(key)!.slice().sort();
    for (const value of values) {
      pairs.push(goQueryEscape(key) + '=' + goQueryEscape(value));
    }
  }
  return pairs.join('&');
}

// ── Web Crypto helpers ────────────────────────────────────────────

const encoder = new TextEncoder();

async function sha256Hex(data: string): Promise<string> {
  const buffer = await crypto.subtle.digest('SHA-256', encoder.encode(data));
  return Array.from(new Uint8Array(buffer))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

async function hmacSha256Hex(key: string, data: string): Promise<string> {
  const cryptoKey = await crypto.subtle.importKey(
    'raw',
    encoder.encode(key),
    { name: 'HMAC', hash: 'SHA-256' },
    false,
    ['sign'],
  );
  const signature = await crypto.subtle.sign('HMAC', cryptoKey, encoder.encode(data));
  return Array.from(new Uint8Array(signature))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

// ── Build headers ─────────────────────────────────────────────────

export async function buildSecurityHeaders(
  method: string,
  path: string,
  rawBody: string,
  secret: string,
): Promise<Record<string, string>> {
  const u = new URL(path, 'http://localhost');
  const bodyHash = await sha256Hex(rawBody);
  const timestamp = getAdjustedTimestamp();
  const nonce = generateNonce();

  const canonical = [
    method.toUpperCase(),
    u.pathname || '/',
    canonicalQuery(u.searchParams),
    bodyHash,
    String(timestamp),
    nonce,
  ].join('\n');

  const signature = await hmacSha256Hex(secret, canonical);

  return {
    'X-Req-Timestamp': String(timestamp),
    'X-Req-Nonce': nonce,
    'X-Req-Body-Hash': bodyHash,
    'X-Req-Signature': signature,
  };
}
